import { useTranslation } from 'react-i18next';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';

import {
  hsaContributionsIntial,
  updatePreviousContribution,
  updatePreviousContributionAmount,
  updateContributionDetails,
  updateScheduledContribution,
  updateContributeType,
  updateResetAllEmployeeChanges,
} from 'enrollment/services';
import {
  PreviousContributionCardStyled,
  TitleStyled,
  DescriptionStyled,
  RadioGroupStyled,
  ContributionInputSectionStyled,
  InfoMessageStyled,
  InfoMessageTitleStyled,
  InfoMessageDescriptionStyled,
  ModalTitleStyled,
  ModalDescriptionStyled
} from './PreviousContributionStyled';
import { PsButtonTuner, PsModalTuner, PsNumericTuner, PsRadioTuner } from 'stencilSetup';
import { convertCurrency } from 'enrollment/utils';
import { calculateContributionAdjustments} from '../../utils/utils';
import { INITIAL_CONTRIBUTION_ADJUSTMENTS } from 'enrollment/components/ContributionsCard/utils/constants';
import { PSBannerWrapper } from 'enrollment/components/Shared';

declare const bootstrap: any;

export function PreviousContribution(props: any) {
  const dispatch = useDispatch();
  const newHSAData = useSelector(hsaContributionsIntial);
  const newHSADataPrevAmount = typeof newHSAData.previous_org_contribution_amount === 'number'
    ? newHSAData.previous_org_contribution_amount.toFixed(2)
    : newHSAData.previous_org_contribution_amount;

  const irsYear = newHSAData.irs_year;
  const familyIrsMax = newHSAData.irs_for_family;
  const { t } = useTranslation('pages.financial');
  const { initialContributionData, setContributionAdjustments, prevContributionGiven, resetHsaCalculationValues } = props;
  const [prevOrgContributionAmount, setPrevOrgContributionAmount] = useState(0);
  const [isEditing, setIsEditing] = useState(true);
  const submitContributionSchedule = newHSAData?.disable_hsa_form;
  const [prevOrgContribution, setPrevOrgContribution] = useState<boolean | null | undefined>(newHSAData.previous_org_contribution);
  const reCalculationContributions = useRef(false);
  
  useEffect(() => {
      if (newHSAData.previous_org_contribution !== undefined) {
        setPrevOrgContribution(newHSAData.previous_org_contribution);
        setPrevOrgContributionAmount(Number(newHSAData.previous_org_contribution_amount));
      }
      if (!reCalculationContributions.current && Number(newHSAData.previous_org_contribution_amount) > 0) {
        setIsEditing(false);
        recalculateContributions(newHSAData.previous_org_contribution_amount);
        reCalculationContributions.current = true;
      }
      if (newHSAData.previous_org_contribution === undefined) {
        setPrevOrgContribution(undefined);
      }
  }, [newHSAData]);
  
  useEffect(() => {
    return () => {
      dispatch(updatePreviousContribution(undefined));
      dispatch(updateContributeType(''));
    };
  }, [dispatch]);

  useEffect(() => {
    if(newHSAData.disable_hsa_form) {
      setIsEditing(false);
    }
  }, [newHSAData.disable_hsa_form]);
  
  const handleOptionChange = (event: any) => {
    const { value } = event.target;
    setPrevOrgContribution(value);
    if(newHSAData.previous_org_contribution === undefined) {
      dispatch(updatePreviousContribution(value));
      setIsEditing(true);
    } else {
      const modal = new bootstrap.Modal(document.getElementById('reset-changes-modal'));
      modal.show();
    }
  };

  const handleModalSubmit = () => {
    // use actual initial data
    const employeeContributionDetails = initialContributionData.data.employee_contribution_details;
    const employerContributionDetails = initialContributionData.data.employer_contribution_details;
    const initialContributionDetails = {
      employeeContribution: employeeContributionDetails.calculated_subscriber_max,
      employerTotalFrequencyContribution: employerContributionDetails.total_org_contribution_amount,
      employerContributionFrequencyAmount: employerContributionDetails.contribution_frequency_amount,
      employerLumpSumAmount: employerContributionDetails.total_er_lump_sum_contribution_amount
    };
    setContributionAdjustments(INITIAL_CONTRIBUTION_ADJUSTMENTS);
    setPrevOrgContributionAmount(0);
    setIsEditing(true);
    resetHsaCalculationValues(true);
    
    batch(() => {
      dispatch(updateResetAllEmployeeChanges(true));      
      dispatch(updatePreviousContribution(prevOrgContribution));
      dispatch(updatePreviousContributionAmount(0));
      dispatch(updateContributionDetails(initialContributionDetails));
      dispatch(updateScheduledContribution({ scheduled_contribution: initialContributionData.data.scheduled_contribution, disable_hsa_form: false }));
    });
  };

  const handleModalClose = () => {
    setPrevOrgContribution(newHSAData.previous_org_contribution);
  };

  const handleAmountChange = (event: KeyboardEvent<HTMLPsNumericTunerElement>) => {
    const value = event.target.value;
    setPrevOrgContributionAmount(Number(value));
  };

  const handleSaveCancelPrevContribution = (data: string) => {
    if (data === "save") {
      batch(() => {
        dispatch(updateResetAllEmployeeChanges(true));
        dispatch(updatePreviousContributionAmount(prevOrgContributionAmount));
      });
      recalculateContributions(prevOrgContributionAmount);
    }
    setIsEditing(false);
  };

  const recalculateContributions = (prevContribution: number) => {
    const contributionAdjustmentResult = calculateContributionAdjustments(prevContribution, initialContributionData.data); // calculations should be done on the initial data coming form the new API call
    setContributionAdjustments(contributionAdjustmentResult);
    dispatch(updateContributionDetails(contributionAdjustmentResult));
    dispatch(updateScheduledContribution({ scheduled_contribution: contributionAdjustmentResult.scheduledContributions, disable_hsa_form: false }));
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };
  const isAnyOptionSelected = newHSAData.previous_org_contribution !== undefined;

  return (
    <div className={`${submitContributionSchedule ? 'disabled' : ''} px-3_5 px-md-5 pb-3 mt-3`} data-testid="previous-contribution-card">
      <PreviousContributionCardStyled>
        <TitleStyled className="mb-2" data-testid="prev-contribution-title">
          {t('previousContribution.title', { irsYear })}
        </TitleStyled>
        <DescriptionStyled data-testid="prev-contribution-description"  isAnySelected={isAnyOptionSelected}>
          {t('previousContribution.description')}
        </DescriptionStyled>
        <RadioGroupStyled>
          <PsRadioTuner
            inputId="previous-contribution-no"
            data-testid="prev-contribution-no"
            name="radio-previous-contribution"
            label={t('previousContribution.no')}
            value={false}
            checked={prevOrgContribution === false}
            onPsChange={handleOptionChange}
          />
          <PsRadioTuner
            inputId="previous-contribution-yes"
            data-testid="prev-contribution-yes"
            name="radio-previous-contribution"
            label={t('previousContribution.yes')}
            value={true}
            checked={prevOrgContribution === true}
            onPsChange={handleOptionChange}
          />
          <PsRadioTuner
            inputId="previous-contribution-dont-know"
            data-testid="prev-contribution-dont-know"
            name="radio-previous-contribution"
            label={t('previousContribution.dontKnow')}
            value={null}
            checked={prevOrgContribution === null}
            onPsChange={handleOptionChange}
          />
        </RadioGroupStyled>
        {newHSAData?.previous_org_contribution === true && (
          <ContributionInputSectionStyled className="row md-1">
            <div className= "previousContributionAmt col-5 mr-4">
              <PsNumericTuner
                className="previous-contribution-amount"
                inputId="previous-contribution-amount"
                data-testid="prev-contribution-input-field"
                label={t('previousContribution.contributionSoFar')}
                value={newHSADataPrevAmount}
                onKeyUp={handleAmountChange}
                disabled={!isEditing}
                icon-left
                icon="currency-dollar"
                helpText={`${t('previousContribution.maxContribution')} ${convertCurrency(String(familyIrsMax))}`}
                acceptDecimal={true}
              />
            </div>
            <div className="mt-2_5 d-flex">
              <PsButtonTuner
                id="save_edit_btn"
                data-testid="save_edit_btn"
                level="secondary"
                label={t(isEditing ? 'previousContribution.save' : 'previousContribution.edit')}
                disabled={isEditing ? (prevOrgContributionAmount <= 0 || prevOrgContributionAmount > Number(familyIrsMax)) : false}
                onClick={isEditing ? () => handleSaveCancelPrevContribution("save") : handleEditClick}
                buttonId="save_edit_btn"
              />
              {isEditing && prevContributionGiven && (
                <div className='ml-4'>
                  <PsButtonTuner
                    id="cancel-btn"
                    data-testid="cancel-btn"
                    level="tertiary"
                    label={t('previousContribution.cancel')}
                    onClick={() => handleSaveCancelPrevContribution("cancel")}
                    buttonId="cancel-btn"
                  />
                </div>
              )}
            </div>
          </ContributionInputSectionStyled>
        )}
        {newHSAData?.previous_org_contribution === null && (
          <InfoMessageStyled>
            <InfoMessageTitleStyled className="mb-2" data-testid="info-message-title">
              {t('previousContribution.infoMessageTitle')}
            </InfoMessageTitleStyled>
            <InfoMessageDescriptionStyled data-testid="info-message-description">
              {t('previousContribution.infoMessageDescription')}
            </InfoMessageDescriptionStyled>
          </InfoMessageStyled>
        )}
        {isEditing && prevContributionGiven && (
          <div className='mt-4'>
            <PSBannerWrapper
              id="change-warning-banner"
              message={t('contributionAdjustments.warningMessage')}
              level='warning'
              icon
            />
          </div>
        )}
        <PsModalTuner
          data-testid="reset-changes-modal"
          modalId="reset-changes-modal"
          modalTitle={t('previousContribution.modalTitle')}
          primaryActive
          primaryButton={t('previousContribution.confirm')}
          secondaryButton={t('previousContribution.cancel')}
          onPsModalSubmit={handleModalSubmit}
          onPsModalDismiss={handleModalClose}
          modalStatic
        >
          <ModalTitleStyled className='mt-3'>{t('previousContribution.modalHeader')}</ModalTitleStyled>
          <ModalDescriptionStyled>{t('previousContribution.modalDescription')}</ModalDescriptionStyled>
        </PsModalTuner>
      </PreviousContributionCardStyled>
    </div>
  );
}
